import React, {useMemo, useState} from 'react';
import {type AutocompleteProps, type TextFieldProps} from '@mui/material';
import type {AttributeSetId, IAttributeSet} from '@netx/core-attribute-set';
import Chip, {ChipSkeleton} from '@netx/core-components/Chip';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import SelectField, {SelectFieldProps} from '@netx/core-components/SelectField';
import {styled} from '@netx/core-components/Theme';
import {i18n} from '@netx/core/i18n';

export * from '@netx/core-components/SelectField/selectFieldClasses';

const getSafeValues = (values?: any[]) =>
	values && Array.isArray(values) ? values : [];

const AttributeSetSelectorRoot = styled(SelectField)(({theme}) => ({
	flex: '0 0 auto',
	// padding: theme.spacing(1, 2),
	width: '100%',
})) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: SelectFieldProps<string, Multiple, DisableClearable, false>,
) => JSX.Element;

// We want our sets to be grouped by type (client, user, system) and in that order
const getSetOptionsInOrder = (sets: IAttributeSet[]) => {
	const userLabel = i18n.t('i18n.user');
	const systemLabel = i18n.t('i18n.system');
	return sets
		.filter((set) => set.active === 1)
		.map((set): Option => {
			const group = ['mandatory', 'all'].includes(set.id)
				? ''
				: set.id.match(/^user-/)
					? userLabel
					: systemLabel;
			return {
				group,
				id: set.id,
				active: set.active,
				name: set.name,
			};
		})
		.sort((a, b) => {
			// "no group" wins
			// followed by user sets
			// then system
			if (a.group === '' && (b.group === userLabel || b.group !== ''))
				return -1;
			if (a.group === userLabel && b.group === systemLabel) return -1;
			return 1;
		});
};

export interface SetSelectorProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
> extends Partial<
	Omit<SelectFieldProps<string, Multiple, DisableClearable, false>, 'options'>
> {
	attributeSets: IAttributeSet[];
	allowCustom?: boolean;
	enforceAttributeSets?: string[];
	variant?: TextFieldProps['variant'];
}

export interface Option {
	group: string;
	id: AttributeSetId;
	active?: 0 | 1;
	name: string;
}

export const SetSelectorPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	{
		attributeSets,
		enforceAttributeSets,

		allowCustom,
		label,
		multiple,
		onChange,
		value,
		...props
	}: SetSelectorProps<Multiple, DisableClearable>,
	ref: any,
) => {
	const [inputValue, setInputValue] = useState('');

	const attributeSetsDetails = useMemo(() => {
		return getSetOptionsInOrder(attributeSets).concat(
			allowCustom
				? [
						{
							group: i18n.t('i18n.custom'),
							id: 'custom',
							active: 1,
							name: i18n.t('i18n.custom'),
						},
					]
				: [],
		);
	}, [attributeSets]);

	const options = useMemo(() => {
		return attributeSetsDetails.map((set) => set.id);
	}, [attributeSetsDetails]);

	const getOptionLabel: Required<
		AutocompleteProps<string, Multiple, DisableClearable, false>
	>['getOptionLabel'] = (option) =>
		attributeSetsDetails.find((set) => set.id === option)?.name || '';

	const groupBy: Required<
		AutocompleteProps<string, Multiple, DisableClearable, false>
	>['groupBy'] = (option) =>
		attributeSetsDetails.find((set) => set.id === option)?.group || '';

	const renderTags = useConstCallback<
		Required<
			AutocompleteProps<string, Multiple, DisableClearable, false>
		>['renderTags']
	>((values, getTagProps) => {
		values = getSafeValues(values);
		return props.loadingValue ? (
			<ChipSkeleton count={3} />
		) : (
			values.map((value, index) => {
				if (!value) return null;
				const label = getOptionLabel(value);
				const tagProps = getTagProps({index}) as any;
				return (
					<Chip
						{...tagProps}
						disabled={props.disabled}
						onDelete={
							enforceAttributeSets?.includes(value)
								? undefined
								: tagProps.onDelete
						}
						color="secondary"
						key={`${value}`}
						label={label}
						title={label}
					/>
				);
			})
		);
	});

	const handleInputChange = useConstCallback(
		(_: any, value: string, reason: string) => {
			if (reason === 'reset') return;
			setInputValue(value);
		},
	);

	const placeholder = !multiple ? i18n.t('i18n.pleaseSelect') : undefined;

	return (
		<AttributeSetSelectorRoot<Multiple, DisableClearable>
			ref={ref}
			id="attribute-set-selection"
			label={
				label ??
				(multiple ? i18n.t('i18n.attributeSets') : i18n.t('i18n.attributeSet'))
			}
			multiple={multiple}
			name="set-selector"
			options={options}
			placeholder={placeholder}
			{...props}
			inputValue={
				!multiple && inputValue === '' && value
					? getOptionLabel(value as string)
					: inputValue
			}
			openOnFocus={false}
			value={value}
			getOptionLabel={getOptionLabel}
			groupBy={groupBy}
			onBlur={() => {
				setInputValue('');
			}}
			onChange={(event, value, reason, details) => {
				setInputValue('');
				onChange?.(event, value, reason, details);
			}}
			onInputChange={handleInputChange}
			renderTags={renderTags}
			InputProps={{
				...props.InputProps,
				InputLabelProps: {
					...props.InputProps?.InputLabelProps,
					shrink: true,
				},
			}}
		/>
	);
};

export const SetSelector = React.forwardRef(SetSelectorPrimitive) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: SetSelectorProps<Multiple, DisableClearable>,
) => JSX.Element;

export default React.memo(SetSelector) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
>(
	props: SetSelectorProps<Multiple, DisableClearable>,
) => JSX.Element;
